<template>
  <FreeBottomSheet
    ref="bottomSheetRef"
    :name="name"
    @onOpen="handleOpen"
    @onClose="handleClose"
  >
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <h2
          class="text-text-neutral-primary text-base font-medium leading-[22px]"
        >
          {{ headerTitle }}
        </h2>
        <template #rightAction>
          <FreeCloseButton
            @click="handleCloseTap(hide)"
          />
        </template>
      </FreeHeader>

      <!--
        Chip-row from polygon (1-to-1 markup). All four chips stay in DOM; the
        flex-basis / opacity transitions produce the "animated" feel.
      -->
      <div
        class="chip-row flex h-14 w-full flex-nowrap items-center px-4"
      >
        <!-- Dep chip -->
        <div
          class="chip bg-situational-lightgrey relative h-11 rounded-xl"
          :class="depChipClass"
          @click="onChipTap('dep')"
        >
          <div
            class="chip__layer pl-3 pr-3"
            :class="
              activeField === 'dep' ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <input
              ref="depInputRef"
              :value="inputValue"
              type="text"
              :placeholder="$t('main.cities.departurePlaceholder')"
              class="text-text-neutral-primary placeholder:text-situational-darkgrey w-full min-w-0 bg-transparent text-base font-medium outline-none"
              @input="onQueryInput"
              @compositionend="onQueryInput"
            />
            <button
              v-if="activeField === 'dep' && inputValue.length > 0"
              type="button"
              class="bg-situational-darkgrey/15 text-text-neutral-primary ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
              @mousedown.prevent
              @click.stop="onClearQuery"
            >
              <CloseIcon class="h-2.5 w-2.5" />
            </button>
          </div>
          <div
            class="chip__layer justify-center"
            :class="
              activeField !== 'dep' ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <span
              class="text-base font-medium"
              :class="
                currentDep
                  ? 'text-text-neutral-primary'
                  : 'text-situational-darkgrey'
              "
              >{{ currentDep?.code ?? depPlaceholderCode }}</span
            >
          </div>
        </div>

        <!-- Arr chip -->
        <div
          class="chip bg-situational-lightgrey relative h-11 rounded-xl"
          :class="arrChipClass"
          @click="onChipTap('arr')"
        >
          <div
            class="chip__layer pl-3 pr-3"
            :class="
              activeField === 'arr' ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <input
              ref="arrInputRef"
              :value="inputValue"
              type="text"
              :placeholder="$t('main.cities.arrivalPlaceholder')"
              class="text-text-neutral-primary placeholder:text-situational-darkgrey w-full min-w-0 bg-transparent text-base font-medium outline-none"
              @input="onQueryInput"
              @compositionend="onQueryInput"
            />
            <button
              v-if="activeField === 'arr' && inputValue.length > 0"
              type="button"
              class="bg-situational-darkgrey/15 text-text-neutral-primary ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
              @mousedown.prevent
              @click.stop="onClearQuery"
            >
              <CloseIcon class="h-2.5 w-2.5" />
            </button>
          </div>
          <div
            class="chip__layer justify-center"
            :class="
              activeField !== 'arr' ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <span
              class="text-base font-medium"
              :class="
                currentArr
                  ? 'text-text-neutral-primary'
                  : 'text-situational-darkgrey'
              "
              >{{ currentArr?.code ?? arrPlaceholderCode }}</span
            >
          </div>
        </div>

        <!-- Dates chip -->
        <div
          class="chip bg-situational-lightgrey relative h-11 rounded-xl"
          :class="datesChipClass"
          @click="onChipTap('dates')"
        >
          <div
            class="chip__layer pl-3 pr-3"
            :class="
              activeField === 'dates' ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <span
              class="text-base font-medium"
              :class="
                internalDates.length > 0
                  ? 'text-text-neutral-primary'
                  : 'text-situational-darkgrey'
              "
              >{{ datesActiveLabel }}</span
            >
          </div>
          <div
            class="chip__layer justify-center"
            :class="
              datesIconLayerActive ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <CalendarIcon class="text-situational-darkgrey h-5 w-5" />
          </div>
          <div
            class="chip__layer pl-3 pr-3"
            :class="
              datesTextLayerActive ? 'chip__layer--on' : 'chip__layer--off'
            "
          >
            <span class="text-text-neutral-primary text-base font-medium">{{
              datesCollapsedLabel
            }}</span>
          </div>
        </div>

        <!-- Passengers chip — always visible -->
        <div
          class="chip chip--mini-pax bg-situational-lightgrey relative h-11 rounded-xl"
          :class="
            activeField === 'passengers'
              ? 'border-background-positive-primary border'
              : 'cursor-pointer'
          "
          @click="onChipTap('passengers')"
        >
          <div class="chip__layer chip__layer--on justify-center gap-1">
            <PersonIcon class="text-situational-darkgrey h-5 w-5 shrink-0" />
            <span
              class="text-text-neutral-primary text-base font-medium"
              >{{ passengersTotal }}</span
            >
          </div>
        </div>
      </div>

      <!-- Calendar sub-header: airline filter + weekdays. v-show keeps in DOM. -->
      <div v-show="activeField === 'dates'" class="calendar-subheader">
        <div class="px-4">
          <CalendarFilter
            v-if="!props.multiTripMode && calendar.getAirlines()?.length"
            :airlines="calendar.getAirlines()"
            :is-direct-flights="calendar.isDirectFligths.value"
            :selected-airline="selectedAirline"
            @onFilterByAirline="onFilterByAirline"
            @onAirlinesListBottomSheet="
              bottomSheet.show(airlineFilterSheetName)
            "
          />
        </div>
        <div class="px-4">
          <div class="bg-background-neutral-primary h-px rounded-full" />
        </div>
        <CalendarWeekdays />
      </div>

      <!-- Passengers sub-header: cabin / youth tabs. v-show keeps in DOM. -->
      <div v-show="activeField === 'passengers'" class="px-4 py-2">
        <PassengersClassesTab
          :active-tab="cabinsDraft.state.value.cabinClass"
          :is-youth="cabinsDraft.state.value.isYouth"
          :source="source"
          @onTabChange="updateCabinClass"
        />
      </div>
    </template>

    <template #default="{ hide }">
      <Transition name="content-fade" mode="out-in">
        <!-- ===== Cities (dep / arr) ===== -->
        <div
          v-if="activeField === 'dep' || activeField === 'arr'"
          key="cities"
          class="flex min-h-[70vh] flex-col gap-4 px-4 pb-5"
        >
          <FreeLoader
            v-if="citiesSearchModel.isLoading.value"
            class="mx-auto mt-12"
          />
          <div
            v-else-if="!query && historyVisible"
            class="bg-background-neutral-secondary mt-4 rounded-2xl px-4 pt-4"
          >
            <p class="text-text-neutral-tertiary text-xs font-medium">
              {{ $t('main.cities.searchHistory') }}
            </p>
            <button
              v-for="(city, index) in historyCities"
              :key="`history-${city.code}`"
              type="button"
              class="flex w-full items-center gap-3 py-3 text-left"
              :class="
                index < historyCities.length - 1
                  ? 'border-situational-lightgrey border-b'
                  : ''
              "
              @click="handleCitySelect(city)"
            >
              <div class="flex flex-1 flex-col gap-[2px]">
                <p
                  class="text-text-neutral-primary text-base font-semibold leading-[22px]"
                >
                  {{ city.name }}
                </p>
                <p
                  class="text-text-neutral-quaternary text-[13px] leading-[18px]"
                >
                  {{ city.country?.name }}
                </p>
              </div>
              <span
                class="text-text-neutral-quaternary text-base leading-[22px]"
                >{{ city.code }}</span
              >
            </button>
          </div>
          <div
            v-else-if="!query && popularRoutes.length > 0"
            class="bg-background-neutral-secondary mt-4 rounded-2xl px-4 pt-4"
          >
            <p class="text-text-neutral-tertiary text-xs font-medium">
              {{ $t('main.cities.popularDestinations') }}
            </p>
            <button
              v-for="(city, index) in popularRoutes"
              :key="`popular-${city.code}`"
              type="button"
              class="flex w-full items-center gap-3 py-3 text-left"
              :class="
                index < popularRoutes.length - 1
                  ? 'border-situational-lightgrey border-b'
                  : ''
              "
              @click="handleCitySelect(city)"
            >
              <div class="flex flex-1 flex-col gap-[2px]">
                <p
                  class="text-text-neutral-primary text-base font-semibold leading-[22px]"
                >
                  {{ city.name }}
                </p>
                <p
                  class="text-text-neutral-quaternary text-[13px] leading-[18px]"
                >
                  {{ city.country?.name }}
                </p>
              </div>
              <span
                class="text-text-neutral-quaternary text-base leading-[22px]"
                >{{ city.code }}</span
              >
            </button>
          </div>
          <div
            v-else-if="searchResultRows.length > 0"
            class="bg-background-neutral-secondary mt-4 rounded-2xl px-4 pt-4"
          >
            <p class="text-text-neutral-tertiary text-xs font-medium">
              {{ $t('main.cities.title') }}
            </p>
            <button
              v-for="(row, index) in searchResultRows"
              :key="row.key"
              type="button"
              class="flex w-full items-center gap-3 py-3 text-left"
              :class="
                index < searchResultRows.length - 1
                  ? 'border-situational-lightgrey border-b'
                  : ''
              "
              @click="handleCitySelect(row.location)"
            >
              <img
                v-if="row.isAirport"
                src="@/assets/images/plane.svg"
                alt=""
                width="20"
                height="20"
              />
              <div class="flex flex-1 flex-col gap-[2px]">
                <p
                  class="text-text-neutral-primary text-base font-semibold leading-[22px]"
                >
                  {{ row.location.name }}
                </p>
                <p
                  class="text-text-neutral-quaternary text-[13px] leading-[18px]"
                >
                  {{ row.location.country?.name }}
                </p>
              </div>
              <span
                class="text-text-neutral-quaternary text-base leading-[22px]"
                >{{ row.location.code }}</span
              >
            </button>
          </div>
          <EmptyResults
            v-else-if="citiesSearchModel.isEmptyResults.value"
            class="mt-4"
            :title="t('citySelectBottomSheet.emptySearchResultsTitle')"
            :description="t('citySelectBottomSheet.tryAgain')"
          >
            <template #action>
              <button
                type="button"
                class="text-button-primary-default text-base font-semibold"
                @click="onResetCitiesQuery"
              >
                {{ t('citySelectBottomSheet.clearInput') }}
              </button>
            </template>
          </EmptyResults>
        </div>

        <!-- ===== Calendar ===== -->
        <div
          v-else-if="activeField === 'dates'"
          key="calendar"
          class="bg-background-neutral-primary flex min-h-[70vh] flex-col gap-2 px-4 pb-24 pt-4"
        >
          <CalendarContainer
            :selected-dates="internalDates"
            :mode="calendarMode"
            :prices="getPrices ?? {}"
            :min-date="calendarMinDate"
            :number-of-months="12"
            is-new-calendar-version
            :is-filtered="!!selectedAirline"
            @onDateChange="handleDateChange"
            @onCalendarIntersect="onCalendarIntersect"
          />
          <FullScreenLoader
            v-if="isFilterChangeLoader"
            class="overflow-hidden rounded-3xl"
          >
            <CalendarLoader :airline="selectedAirline" />
          </FullScreenLoader>
          <div
            class="sticky bottom-[26px] left-0 right-0 flex w-full"
            :class="{ 'pointer-events-none': internalDates.length === 0 }"
          >
            <Transition name="slide-up">
              <FreeButton
                v-if="internalDates.length > 0"
                type="button"
                class="green-main-button flex h-14 w-full flex-col text-lg font-semibold"
                size="sm"
                @click="commitDates"
              >
                <div v-if="hasSelectedDatesPrices">
                  <i18n-t
                    class="text-lg font-semibold text-white"
                    tag="p"
                    keypath="main.searchFormCalendar.selectedDateText"
                  >
                    <template #price>
                      <span v-price="selectedDatesPrice ?? 0" />
                    </template>
                  </i18n-t>
                  <p class="text-2xs font-medium text-white">
                    {{ commitDatesLabel }}
                  </p>
                </div>
                <p v-else class="text-lg font-semibold text-white">
                  {{ $t('general.select') }} {{ commitDatesLabel }}
                </p>
              </FreeButton>
            </Transition>
          </div>
          <FilterAirlinesBottomSheet
            :name="airlineFilterSheetName"
            :airlines="calendar.getAirlines()"
            :is-direct-flights="calendar.isDirectFligths.value"
            :selected-airline="selectedAirline"
            @onAirlineClick="onAirlineClick"
            @onResetFilter="onResetFilter"
          />
        </div>

        <!-- ===== Passengers ===== -->
        <!--
          The sheet is forced to its max-height via the
          `--free-ui-bottom-sheet-min-height` CSS variable (see scoped
          style below), and the FreeBottomSheet's content slot is now
          `flex-1`, so the slot fills the visible area minus the header.
          `h-full` makes this panel match the slot exactly and `mt-auto`
          drops the submit button to the very bottom — no scroll, no
          height jump between fields.
        -->
        <div
          v-else-if="activeField === 'passengers'"
          key="passengers"
          class="relative flex flex-1 flex-col gap-3 px-4 pb-4 pt-2"
        >
          <SelectPassengers
            class="w-full"
            :is-youth-tab="cabinsDraft.state.value.isYouth"
            :passengers="passengersCounter.state.value"
            :source="source"
            @onPassengersChange="passengersCounter.updatePassengers"
            @onChangeError="onPassengersChangeError"
          />
          <Transition appear name="slide-up">
            <FreeButton
              type="button"
              class="green-main-button mt-auto flex h-14 w-full flex-col text-lg font-semibold"
              @click="commitPassengers(hide)"
            >
              {{ $t('main.findTickets') }}
            </FreeButton>
          </Transition>
          <!--
            Alert is absolutely positioned just above the submit button so
            it sits clear of the OS gesture bar on all screen sizes. The
            panel wrapper is `relative` to anchor it.
          -->
          <FreeAlert
            ref="alertRef"
            class="bottom-20 w-[90%] self-center"
          >
            <template #icon>
              <img
                src="@/assets/images/red-warning.svg"
                width="24"
                height="24"
                alt=""
              />
            </template>
            <template #default="{ content }">
              <p class="text-text-neutral-primary">{{ content }}</p>
            </template>
          </FreeAlert>
        </div>
      </Transition>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    FreeAlert,
    FreeBottomSheet,
    FreeButton,
    FreeCloseButton,
    FreeHeader,
    FreeLoader,
    FullScreenLoader,
    CalendarContainer,
    CalendarWeekdays,
    EmptyResults,
  } from 'freedom-shared';
  import {
    citySearchHistory,
    controllerModel,
    dateUtils,
    passengersCounterModel,
    searchCities,
    searchForm as searchFormState,
    searchFormCalendar,
    useDraftState,
    type CabinClass,
    type MappedCity,
    type MappedLocation,
    type Passengers,
  } from 'shared';
  import type { LowPrices } from '@aviata/platform-client';
  import type { Dict } from '@aviata/platform-types';

  import {
    CalendarFilter,
    CalendarLoader,
    FilterAirlinesBottomSheet,
  } from '@/widgets/search-calendar';

  import { calendarBase, calendarLib } from '@/features/calendar';
  import { PassengersClassesTab } from '@/features/passengers-classes-tab';
  import { SelectPassengers } from '@/features/select-passengers';

  import { api, fetchCities } from '@/shared/api';
  import { analyticsEventsLib, focusAndOpenKeyboard } from '@/shared/lib';

  import CalendarIcon from '@/assets/images/calendar-icon.svg?component';
  import CloseIcon from '@/assets/images/close-icon.svg?component';
  import PersonIcon from '@/assets/images/person-icon.svg?component';

  type FieldType = 'dep' | 'arr' | 'dates' | 'passengers';
  type FieldOpenTrigger = 'sheet_open' | 'auto_advance' | 'chip_tap';

  const props = withDefaults(
    defineProps<{
      name: string;
      source: string;
      initialField: FieldType;
      dep: MappedLocation | null;
      arr: MappedLocation | null;
      dates: string[];
      passengers: Passengers;
      cabinClass: CabinClass;
      isYouth: boolean;
      multiTripMode?: boolean;
    }>(),
    {
      multiTripMode: false,
    },
  );

  const emit = defineEmits<{
    onCitySelect: [field: 'dep' | 'arr', city: MappedLocation];
    onDatesCommit: [dates: string[]];
    onCabinClassChange: [value: CabinClass];
    onSetIsYouth: [value: boolean];
    onPassengersStateChange: [value: Passengers];
    onSubmitFromSheet: [];
    onSelectAirline: [airline: Dict.Airlines.Airline | null];
  }>();

  const { t } = useI18n();

  // ------------------------------------------------------------------
  // Active-field state, drives chip styles / content / analytics.
  // ------------------------------------------------------------------

  const bottomSheetRef = ref<{ contentRef?: HTMLDivElement } | null>(null);

  const activeField = ref<FieldType>(props.initialField);
  const fieldsCompleted = ref<Set<FieldType>>(new Set());
  const initialFieldOnOpen = ref<FieldType>(props.initialField);
  const fieldOpenedAt = ref<number>(0);
  const sheetOpenedAt = ref<number>(0);
  const submittedSinceOpen = ref<boolean>(false);

  const headerTitle = computed(() => {
    if (activeField.value === 'dep') {
      return t('main.cities.departure');
    }
    if (activeField.value === 'arr') {
      return t('main.cities.arrival');
    }
    if (activeField.value === 'dates') {
      return t('main.dates.title');
    }
    return t('main.cabinClass.select');
  });

  const currentDep = computed(() => props.dep);
  const currentArr = computed(() => props.arr);

  // ------------------------------------------------------------------
  // Cities (same composables as the shared CitySelectBottomSheet so the
  // result list behaves identically: debounce, history, fetchCities).
  // ------------------------------------------------------------------

  // `inputValue` drives the visible text in the dep/arr input (prefilled with
  // the already-chosen city name on open so the user sees their previous pick).
  // `query` is the *active search term* — only set after the user actually
  // types. Splitting them keeps the history / popular list visible when the
  // input shows a prefilled city name but no new search has been issued.
  const inputValue = ref('');
  const query = ref('');
  const depInputRef = ref<HTMLInputElement>();
  const arrInputRef = ref<HTMLInputElement>();

  const searchHistory =
    citySearchHistory.model.useCitySearchHistory('free-avia');
  const citiesSearchModel = searchCities.model.useCitiesSearch(fetchCities);

  const popularRoutesDep = searchCities.lib.getDeparturePopularRoutes();
  const popularRoutesArr = searchCities.lib.getArrivalPopularRoutes();

  const depPlaceholderCode = popularRoutesDep[0]?.code ?? 'ALA';
  const arrPlaceholderCode = popularRoutesArr[0]?.code ?? 'NQZ';

  const popularRoutes = computed<MappedCity[]>(() =>
    activeField.value === 'dep' ? popularRoutesDep : popularRoutesArr,
  );

  const historyMinCities = 2;
  const historyCities = computed(() => searchHistory.citiesList.value);
  const historyVisible = computed(
    () => historyCities.value.length >= historyMinCities,
  );

  /**
   * Flatten the search response so airports (MappedCity.children) become
   * selectable rows under their parent city, mirroring the legacy
   * CitiesList rendering used by CitySelectBottomSheet.
   */
  const searchResultRows = computed<
    Array<{ key: string; isAirport: boolean; location: MappedLocation }>
  >(() => {
    const rows: Array<{
      key: string;
      isAirport: boolean;
      location: MappedLocation;
    }> = [];
    for (const city of citiesSearchModel.cities.value) {
      rows.push({
        key: `search-${city.code}`,
        isAirport: false,
        location: city,
      });
      if (city.children?.length) {
        for (const airport of city.children) {
          rows.push({
            key: `search-${city.code}-${airport.code}`,
            isAirport: true,
            location: airport,
          });
        }
      }
    }
    return rows;
  });

  // Listen on the raw `input` / `compositionend` events instead of `v-model` —
  // Android IME composition swallows v-model updates until the user taps
  // "Next" on the keyboard, which blocks autocomplete while typing. The
  // classic CitySelectBottomSheet follows the same pattern via FreeSearchInput.
  const runCitiesQuery = (raw: string) => {
    inputValue.value = raw;
    query.value = raw;
    const trimmed = raw.trim();
    citiesSearchModel.setNewQuery(trimmed);
    if (trimmed) {
      citiesSearchModel.searchCities(trimmed);
    } else {
      citiesSearchModel.clearCities();
    }
  };

  const onQueryInput = (event: Event) => {
    runCitiesQuery((event.target as HTMLInputElement).value);
  };

  // ------------------------------------------------------------------
  // Calendar — local draft, committed via emit on "Выбрать". Keeps the
  // unified flow a self-contained surface; the parent merges the dates back
  // into the form via @onDatesCommit.
  // ------------------------------------------------------------------

  const internalDates = ref<string[]>([...(props.dates ?? [])]);
  const calendarMode = ref<searchFormCalendar.config.CalendarMode>(
    searchFormCalendar.config.CALENDAR_MODES.SINGLE,
  );

  const calendarMinDate = computed(
    () => searchFormCalendar.lib.getCalendarDateLimits().min,
  );

  const handleDateChange = (date: string) => {
    if (date < calendarMinDate.value) {
      return;
    }
    const [first, second] = internalDates.value;
    if (
      calendarMode.value === searchFormCalendar.config.CALENDAR_MODES.SINGLE ||
      !first
    ) {
      internalDates.value = [date];
      calendarMode.value = searchFormCalendar.config.CALENDAR_MODES.RANGE;
      return;
    }
    /**
     * Both dates already picked — restart the range from the tapped date
     * (this matches the polygon's expectation: a fresh tap is "departure",
     * not a replacement of the return date).
     */
    if (second) {
      internalDates.value = [date];
      return;
    }
    if (date < first) {
      internalDates.value = [date];
      return;
    }
    internalDates.value = [first, date];
  };

  const datesActiveLabel = computed(() => {
    if (internalDates.value.length === 0) {
      return t('main.dates.title');
    }
    return calendarLib.formatSelectedDates(internalDates.value, false);
  });

  const datesCollapsedLabel = computed(() => {
    if (internalDates.value.length === 0) {
      return '';
    }
    return calendarLib.formatSelectedDates(internalDates.value, false);
  });

  // ------------------------------------------------------------------
  // Passengers — same draft / counter composables that
  // PassengersSelectBottomSheet uses, so behaviour and validation match.
  // ------------------------------------------------------------------

  const cabinsDraft = useDraftState({
    initialDraft: {
      isYouth: props.isYouth,
      cabinClass: props.cabinClass,
    },
  });

  const passengersCounter = passengersCounterModel.usePassengersCounter({
    initialPassengers: searchFormState.config.DEFAULT_PASSENGERS_STATE,
  });

  const alertRef = ref();

  const updateCabinClass = (cabinClass: string, isYouth: boolean) => {
    cabinsDraft.updateDraft({ cabinClass, isYouth });
  };

  const onPassengersChangeError = (payload: { [index: string]: string }) => {
    const list = passengersCounter.passengersList.value;
    const warningText = t(
      searchFormState.lib.getAlertTextKey(payload, list.adt, list.inf),
    );
    if (warningText) {
      alertRef.value?.show(warningText);
    }
  };

  /**
   * The counter keeps both adt/chd/inf AND yth in state because each tab has
   * its own minimum (yth defaults to 1 even on the Economy tab). The chip
   * total must reflect only the *active* tab — otherwise economy shows "2".
   */
  const passengersTotal = computed(() => {
    const list = passengersCounter.passengersList.value;
    if (cabinsDraft.state.value.isYouth) {
      return list.yth ?? 0;
    }
    return list.adt + list.chd + list.inf;
  });

  // ------------------------------------------------------------------
  // Calendar prices + airline filter — ported from SearchCalendar so the
  // unified flow shows the same low-price annotations and filter UI.
  // ------------------------------------------------------------------

  const airlineFilterSheetName = `${props.name}-airline-filter`;
  const filterLoaderDelay = 400;
  const lastMonthIndex = 11;

  const bottomSheet = controllerModel.useBottomSheet();
  const selectedAirline = ref<Dict.Airlines.Airline | null>(null);
  const isFilterChangeLoader = ref(false);

  const calendar = calendarBase.model.useCalendar({
    fetchLowPrices: (params: LowPrices.V2.ExtendedSearchCalendarParams) => {
      return api.lowPrices.getSearchCalendarV2(params).then(res => res.data);
    },
  });

  const depToArrResponse = computed(
    () => calendar.searchCalendarMap.value.depToArrResponse,
  );
  const arrToDepResponse = computed(
    () => calendar.searchCalendarMap.value.arrToDepResponse,
  );

  const getDepPrices = computed(() => {
    if (!depToArrResponse.value) {
      return;
    }
    return calendarLib.getNewPricesArr(
      depToArrResponse.value,
      selectedAirline.value,
    );
  });

  const getArrPrices = computed(() => {
    if (!arrToDepResponse.value) {
      return;
    }
    return calendarLib.getNewPricesArr(
      arrToDepResponse.value,
      selectedAirline.value,
    );
  });

  /**
   * In SINGLE mode the user is picking the outbound date — show dep→arr
   * prices. After the first pick the mode flips to RANGE and the user picks
   * a return date, so we switch to the arr→dep price map.
   */
  const getPrices = computed(() => {
    if (
      calendarMode.value === searchFormCalendar.config.CALENDAR_MODES.SINGLE
    ) {
      return getDepPrices.value;
    }
    return getArrPrices.value;
  });

  const fetchLowPrices = async () => {
    const dep = props.dep?.code;
    const arr = props.arr?.code;
    if (!dep || !arr || dep === arr) {
      return;
    }

    const dateFrom = dateUtils.getTomorrowDate();
    const dateTo = dateUtils.getFutureDate(calendar.getVisibleItems() * 30);

    const params = {
      dep,
      arr,
      isYouth: props.isYouth,
      cabinClass: props.cabinClass,
      passengers: props.passengers,
      dateFrom,
      dateTo,
    };

    await Promise.all([
      calendar.getLowPrices(params),
      calendar.getLowPrices({ ...params, dep: arr, arr: dep, arrToDep: true }),
    ]);
  };

  const onCalendarIntersect = (items: number) => {
    if (items === lastMonthIndex) {
      calendar.setVisibleItems(lastMonthIndex);
    } else {
      calendar.setVisibleItems(items + 2);
    }
    fetchLowPrices();
  };

  const onFilterByAirline = (airline: Dict.Airlines.Airline) => {
    bottomSheet.hide(airlineFilterSheetName);
    if (selectedAirline.value && selectedAirline.value.iata === airline.iata) {
      selectedAirline.value = null;
    } else {
      selectedAirline.value = airline;
    }
    emit('onSelectAirline', selectedAirline.value);
    isFilterChangeLoader.value = true;
    setTimeout(() => {
      isFilterChangeLoader.value = false;
    }, filterLoaderDelay);
  };

  const onAirlineClick = ({
    airline,
    idx,
  }: {
    idx: number;
    airline: Dict.Airlines.Airline;
  }) => {
    onFilterByAirline(airline);
    calendar.moveAirlineToTop(idx);
  };

  const onResetFilter = () => {
    selectedAirline.value = null;
    bottomSheet.hide(airlineFilterSheetName);
  };

  const hasSelectedDatesPrices = computed(() => {
    const [first, second] = internalDates.value;
    if (!first || !getDepPrices.value) {
      return false;
    }
    if (!second) {
      return Boolean(Number(getDepPrices.value[first]?.price));
    }
    return Boolean(
      Number(getDepPrices.value[first]?.price) &&
        Number(getArrPrices.value?.[second]?.price),
    );
  });

  const selectedDatesPrice = computed(() => {
    if (!hasSelectedDatesPrices.value) {
      return null;
    }
    const [first, second] = internalDates.value;
    const depPrice = Number(getDepPrices.value?.[first]?.price ?? 0);
    if (!second) {
      return depPrice;
    }
    const arrPrice = Number(getArrPrices.value?.[second]?.price ?? 0);
    return depPrice + arrPrice;
  });

  const commitDatesLabel = computed(() =>
    calendarLib.formatSelectedDates(
      internalDates.value,
      hasSelectedDatesPrices.value,
    ),
  );

  // ------------------------------------------------------------------
  // Chip styling — mirrors polygon class logic.
  // ------------------------------------------------------------------

  const depChipClass = computed(() => {
    if (activeField.value === 'dep') {
      return 'chip--active border-background-positive-primary border';
    }
    return 'chip--mini cursor-pointer';
  });

  const arrChipClass = computed(() => {
    if (activeField.value === 'arr') {
      return 'chip--active border-background-positive-primary border';
    }
    return 'chip--mini cursor-pointer';
  });

  const datesChipClass = computed(() => {
    const hasDates = internalDates.value.length > 0;
    if (activeField.value === 'dates') {
      return 'chip--active border-background-positive-primary border';
    }
    if (activeField.value === 'passengers' && hasDates) {
      return 'chip--flex cursor-pointer';
    }
    return 'chip--mini cursor-pointer';
  });

  const datesTextLayerActive = computed(
    () => activeField.value === 'passengers' && internalDates.value.length > 0,
  );

  const datesIconLayerActive = computed(
    () => activeField.value !== 'dates' && !datesTextLayerActive.value,
  );

  // ------------------------------------------------------------------
  // Field switching, focus, analytics.
  // ------------------------------------------------------------------

  const getActiveCityInputRef = () => {
    if (activeField.value === 'dep') {
      return depInputRef.value;
    }
    if (activeField.value === 'arr') {
      return arrInputRef.value;
    }
    return null;
  };

  const focusActiveInput = () => {
    nextTick(() => {
      const inputRef = getActiveCityInputRef();
      if (!inputRef) {
        return;
      }
      focusAndOpenKeyboard(inputRef, 100);
      // Select the prefilled city name so the first typed char replaces it.
      // Wait a bit past focusAndOpenKeyboard's own delay or the selection
      // is cleared by the focus event firing afterwards.
      setTimeout(() => {
        if (document.activeElement === inputRef) {
          inputRef.select();
        }
      }, 160);
    });
  };

  const blurActiveInput = () => {
    depInputRef.value?.blur();
    arrInputRef.value?.blur();
  };

  /**
   * Tapping the ✕ on Android caused the page navbar / AI-chat button to
   * jump for one frame because the keyboard collapse and the sheet's
   * `min-height` reflow both happen on the same tick. Blurring before
   * `hide()` lets the keyboard start dismissing on its own animation,
   * which keeps the navbar in place.
   */
  const handleCloseTap = (hide: () => void) => {
    blurActiveInput();
    hide();
  };

  const onResetCitiesQuery = () => {
    inputValue.value = '';
    query.value = '';
    citiesSearchModel.clearCities();
    focusActiveInput();
  };

  const onClearQuery = () => {
    inputValue.value = '';
    query.value = '';
    citiesSearchModel.clearCities();
    focusActiveInput();
  };

  const reportFieldOpen = (field: FieldType, trigger: FieldOpenTrigger) => {
    fieldOpenedAt.value = performance.now();
    analyticsEventsLib.animatedFlowFieldOpen({
      field,
      initial_field: initialFieldOnOpen.value,
      source: props.source,
      trigger,
    });
  };

  const reportFieldCommit = (field: FieldType) => {
    if (fieldOpenedAt.value === 0) {
      return;
    }
    fieldsCompleted.value.add(field);
    analyticsEventsLib.animatedFlowFieldCommit({
      field,
      source: props.source,
      duration_ms: Math.max(
        0,
        Math.round(performance.now() - fieldOpenedAt.value),
      ),
    });
  };

  const resetSheetScroll = () => {
    nextTick(() => {
      const content = bottomSheetRef.value?.contentRef;
      if (content) {
        content.scrollTop = 0;
      }
    });
  };

  const ensureCalendarPricesLoaded = async () => {
    const hasResponses = depToArrResponse.value && arrToDepResponse.value;
    if (!hasResponses) {
      await fetchLowPrices();
    }
    calendar.intersectAirlines();
  };

  watch([() => props.dep?.code, () => props.arr?.code], () => {
    calendar.clearAirlines();
    calendar.clearSearchCalendarMap();
    selectedAirline.value = null;
    if (activeField.value === 'dates') {
      ensureCalendarPricesLoaded();
    }
  });

  /**
   * When the user returns to a chip whose city is already chosen, prefill the
   * input with the city name so it doesn't visually reset to a placeholder.
   * The first typed character will replace the selection (see focusActiveInput).
   */
  const prefillCityQuery = (field: FieldType) => {
    if (field === 'dep') {
      return props.dep?.name ?? '';
    }
    if (field === 'arr') {
      return props.arr?.name ?? '';
    }
    return '';
  };

  const switchField = (
    field: FieldType,
    trigger: 'auto_advance' | 'chip_tap',
  ) => {
    activeField.value = field;
    inputValue.value = prefillCityQuery(field);
    query.value = '';
    citiesSearchModel.clearCities();
    resetSheetScroll();
    if (field === 'dep' || field === 'arr') {
      focusActiveInput();
    }
    if (field === 'dates') {
      ensureCalendarPricesLoaded();
    }
    reportFieldOpen(field, trigger);
  };

  const onChipTap = (field: FieldType) => {
    if (activeField.value === field) {
      return;
    }
    switchField(field, 'chip_tap');
  };

  const handleCitySelect = (city: MappedLocation) => {
    const field = activeField.value === 'dep' ? 'dep' : 'arr';
    emit('onCitySelect', field, city);
    searchHistory.saveCity(city);
    searchHistory.init();
    reportFieldCommit(field);
    citiesSearchModel.clearCities();
    if (field === 'dep') {
      switchField('arr', 'auto_advance');
      return;
    }
    switchField('dates', 'auto_advance');
  };

  const commitDates = () => {
    emit('onDatesCommit', internalDates.value);
    reportFieldCommit('dates');
    switchField('passengers', 'auto_advance');
  };

  const commitPassengers = (hide: () => void) => {
    emit('onSetIsYouth', cabinsDraft.state.value.isYouth);
    emit('onCabinClassChange', cabinsDraft.state.value.cabinClass);
    emit('onPassengersStateChange', passengersCounter.passengersList.value);
    reportFieldCommit('passengers');
    submittedSinceOpen.value = true;
    /**
     * Submit first so the parent's `router.push` to the results page starts
     * immediately; hiding the sheet afterwards lets the slide-down animation
     * run in parallel with the route transition. The opposite order made the
     * main page flash visible before navigation on some devices.
     */
    emit('onSubmitFromSheet');
    hide();
  };

  const handleOpen = () => {
    sheetOpenedAt.value = performance.now();
    initialFieldOnOpen.value = props.initialField;
    activeField.value = props.initialField;
    fieldsCompleted.value = new Set();
    submittedSinceOpen.value = false;

    inputValue.value = prefillCityQuery(props.initialField);
    query.value = '';
    citiesSearchModel.clearCities();
    searchHistory.init();

    internalDates.value = [...(props.dates ?? [])];
    calendarMode.value =
      internalDates.value.length > 1
        ? searchFormCalendar.config.CALENDAR_MODES.RANGE
        : searchFormCalendar.config.CALENDAR_MODES.SINGLE;

    cabinsDraft.updateDraft({
      isYouth: props.isYouth,
      cabinClass: props.cabinClass,
    });
    passengersCounter.updatePassengers(props.passengers);

    selectedAirline.value = null;
    calendar.clearSearchCalendarMap();

    reportFieldOpen(props.initialField, 'sheet_open');
    /**
     * Intentionally do NOT auto-focus dep/arr on first open — that would pop
     * the system keyboard before the sheet finishes sliding up. The legacy
     * CitySelectBottomSheet also waits for an explicit input tap, so this
     * matches the previous UX. Auto-advance between fields keeps focusing
     * because that path runs inside an already-mounted sheet.
     */
    if (props.initialField === 'dates') {
      ensureCalendarPricesLoaded();
    }
  };

  const handleClose = () => {
    /**
     * Blur synchronously so iOS starts dismissing the keyboard at the same
     * tick the sheet starts to slide down — otherwise the sheet animates
     * first and the keyboard collapses behind it, which reads as desynced.
     */
    blurActiveInput();
    calendar.clearSearchCalendarMap();
    selectedAirline.value = null;
    if (submittedSinceOpen.value) {
      return;
    }
    analyticsEventsLib.animatedFlowDismissed({
      last_field: activeField.value,
      fields_completed: Array.from(fieldsCompleted.value),
      source: props.source,
      duration_ms: Math.max(
        0,
        Math.round(performance.now() - sheetOpenedAt.value),
      ),
    });
  };
</script>

<style scoped>
  /*
    Force the bottom sheet to always be at its max height while the
    UnifiedSearchFlow is open. Without this, panels with short content
    (passengers) make the sheet collapse and the height visibly jumps
    when switching from the calendar/cities (which overflow naturally
    and always hit max-height).
  */
  :deep(.bottom-sheet__content) {
    --free-ui-bottom-sheet-min-height: calc(
      100vh - var(--bottom-sheet-top-spacing, 60px) -
        var(--status-bar-height, 0px)
    );
  }

  .chip-row {
    contain: layout style;
  }

  .chip {
    will-change: flex-basis;
    transition:
      flex 280ms cubic-bezier(0.4, 0, 0.2, 1),
      margin 280ms cubic-bezier(0.4, 0, 0.2, 1),
      opacity 200ms ease,
      border-color 150ms;
    min-width: 0;
    margin-left: 8px;
    overflow: hidden;
  }

  .chip:first-child {
    margin-left: 0;
  }

  .chip--active {
    flex: 6 1 0%;
  }

  .chip--flex {
    flex: 6 1 0%;
  }

  .chip--mini {
    flex: 0 0 52px;
  }

  .chip--mini-pax {
    flex: 0 0 48px;
  }

  .chip--gone {
    flex: 0 0 0px;
    opacity: 0;
    margin-left: 0;
    border: none !important;
    pointer-events: none;
  }

  .chip__layer {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-align: left;
    transition: opacity 140ms ease;
  }

  .chip__layer--on {
    opacity: 1;
    pointer-events: auto;
  }

  .chip__layer--off {
    opacity: 0;
    pointer-events: none;
  }

  .content-fade-enter-active,
  .content-fade-leave-active {
    transition: opacity 120ms ease;
  }

  .content-fade-enter-from,
  .content-fade-leave-to {
    opacity: 0;
  }

  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: 0.3s;
  }

  .slide-up-enter-from {
    transform: translateY(100%);
  }

  .slide-up-leave-to {
    transform: translateY(-100%);
  }
</style>
